import React, { useState } from 'react';
import { ArrowRight, Shield, FileText, CheckCircle, Award, Building, Users, AlertTriangle } from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import ConsultationModal from '../../components/ConsultationModal';
import DemoModal from '../../components/DemoModal';

const ComplianceAuditingPage: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isConsultationModalOpen, setIsConsultationModalOpen] = useState(false);
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);

  const complianceFrameworks = [
    {
      icon: Award,
      title: "ISO 27001 Certification",
      description: "Comprehensive information security management system implementation and certification support.",
      features: ["Gap analysis", "Policy development", "Risk assessment", "Certification audit support"]
    },
    {
      icon: Shield,
      title: "GDPR Compliance",
      description: "European data protection regulation compliance assessment and implementation guidance.",
      features: ["Data mapping", "Privacy impact assessments", "Consent management", "Breach procedures"]
    },
    {
      icon: Building,
      title: "SOC 2 Type II",
      description: "Service organization control audits for security, availability, and confidentiality.",
      features: ["Control design", "Evidence collection", "Audit preparation", "Remediation support"]
    },
    {
      icon: FileText,
      title: "PCI DSS Compliance",
      description: "Payment card industry data security standard compliance for organizations handling card data.",
      features: ["Network segmentation", "Encryption requirements", "Access controls", "Vulnerability management"]
    }
  ];

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      isDarkMode ? 'bg-slate-950' : 'bg-slate-50'
    }`}>
      <Header 
        isMenuOpen={isMenuOpen} 
        setIsMenuOpen={setIsMenuOpen}
        isDarkMode={isDarkMode}
        toggleDarkMode={() => setIsDarkMode(!isDarkMode)}
      />

      <ConsultationModal 
        isOpen={isConsultationModalOpen} 
        onClose={() => setIsConsultationModalOpen(false)} 
      />
      <DemoModal 
        isOpen={isDemoModalOpen} 
        onClose={() => setIsDemoModalOpen(false)} 
      />

      {/* Hero Section */}
      <section className={`relative py-20 overflow-hidden ${
        isDarkMode 
          ? 'bg-gradient-to-br from-slate-950 via-slate-900 to-orange-950 text-slate-100' 
          : 'bg-gradient-to-br from-orange-50 via-red-50 to-pink-50 text-slate-900'
      }`}>
        <div className="absolute inset-0">
          <img 
            src="https://images.pexels.com/photos/5668473/pexels-photo-5668473.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1080&fit=crop"
            alt="Compliance Auditing"
            className="w-full h-full object-cover opacity-10"
          />
          <div className={`absolute inset-0 ${
            isDarkMode 
              ? 'bg-gradient-to-r from-slate-950/90 to-orange-950/90' 
              : 'bg-gradient-to-r from-orange-50/90 to-pink-50/90'
          }`} />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <Award className={`h-16 w-16 mx-auto mb-6 ${
              isDarkMode ? 'text-orange-400' : 'text-orange-600'
            }`} />
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Compliance <span className={isDarkMode ? "text-orange-400" : "text-orange-600"}>Auditing</span>
            </h1>
            <p className={`text-xl md:text-2xl mb-8 max-w-3xl mx-auto ${
              isDarkMode ? "text-slate-300" : "text-slate-600"
            }`}>
              Navigate complex regulatory requirements with expert compliance auditing services 
              that ensure your organization meets industry standards and regulations.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => setIsConsultationModalOpen(true)}
                className={`px-8 py-4 rounded-lg font-semibold transition-all duration-300 flex items-center justify-center transform hover:scale-105 shadow-lg ${
                  isDarkMode 
                    ? "bg-orange-600 hover:bg-orange-700 text-white hover:shadow-orange-500/25" 
                    : "bg-orange-600 hover:bg-orange-700 text-white hover:shadow-orange-500/25"
                }`}
              >
                <Shield className="mr-2 h-5 w-5" />
                Start Compliance Audit
                <ArrowRight className="ml-2 h-5 w-5" />
              </button>
              <button 
                onClick={() => setIsDemoModalOpen(true)}
                className={`px-8 py-4 rounded-lg font-semibold transition-all duration-300 border-2 ${
                  isDarkMode 
                    ? "border-orange-400 text-orange-400 hover:bg-orange-400 hover:text-slate-900" 
                    : "border-orange-600 text-orange-600 hover:bg-orange-600 hover:text-white"
                }`}
              >
                View Compliance Guide
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Compliance Frameworks Section */}
      <section className={`py-20 ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${
              isDarkMode ? 'text-slate-100' : 'text-slate-900'
            }`}>
              Compliance Frameworks We Support
            </h2>
            <p className={`text-xl max-w-3xl mx-auto ${
              isDarkMode ? 'text-slate-300' : 'text-slate-600'
            }`}>
              Expert guidance across major compliance standards and regulations
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {complianceFrameworks.map((framework, index) => (
              <div key={index} className={`p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow ${
                isDarkMode ? 'bg-slate-800 border border-slate-700' : 'bg-slate-50 border border-slate-200'
              }`}>
                <framework.icon className={`h-12 w-12 mb-4 ${
                  isDarkMode ? 'text-orange-400' : 'text-orange-600'
                }`} />
                <h3 className={`text-xl font-semibold mb-3 ${
                  isDarkMode ? 'text-slate-100' : 'text-slate-900'
                }`}>{framework.title}</h3>
                <p className={`mb-4 ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                  {framework.description}
                </p>
                <ul className="space-y-2">
                  {framework.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center space-x-2">
                      <CheckCircle className={`h-4 w-4 ${
                        isDarkMode ? 'text-green-400' : 'text-green-600'
                      }`} />
                      <span className={`text-sm ${
                        isDarkMode ? 'text-slate-300' : 'text-slate-600'
                      }`}>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer 
        onConsultationClick={() => setIsConsultationModalOpen(true)}
        onDemoClick={() => setIsDemoModalOpen(true)}
        isDarkMode={isDarkMode}
      />
    </div>
  );
};

export default ComplianceAuditingPage;